import { NETWORK_INFO } from './constants/application.constants';
import { contracts } from './constants/NftContracts';
import { exclamation_triangle_filled } from './icons/icons';
import { ethers } from 'ethers';
import Bowser from 'bowser';
import { TransactionSimulationRequest } from './interfaces/dataTypes.interface';

export const contractBannerColor = (signature: boolean): string => {
  return signature ? 'bg-error-800' : 'bg-secondary-800';
};

export const contractTextColor = (signature: boolean): string => {
  return signature ? 'text-secondary-800' : 'text-white';
};

export const warningIcon = (signature: boolean): JSX.Element => {
  return signature ? exclamation_triangle_filled() : <></>;
};

export const formatUSD = (value: number) => {
  const formatValue = (num: number) => {
    return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  if (value === 0 || !value) {
    return '$0.00';
  }
  if (value < 0.01) {
    return '< $0.01';
  }
  if (value.toString().includes('e')) {
    // formatting to remove scientific notation
    // so instead of 1e-6 we get 0.000001
    const fixedValue = parseFloat(
      value.toFixed(parseInt(value.toString().split('-')[1]))
    );
    return formatValue(fixedValue);
  }
  return formatValue(value);
};

export const capitalizeString = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const formatCurrencyWithoutRounding = (
  value: string,
  decimals?: number | undefined
) => {
  const tokenDecimals = decimals || 18;

  const max256 = parseInt(
    '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff',
    16
  ).toString(16);

  const max160 = parseInt(
    '0xffffffffffffffffffffffffffffffffffffffff',
    16
  ).toString(16);

  if (
    parseInt(value).toString(16) === max256 ||
    parseInt(value).toString(16) === max160
  ) {
    return 'Infinite';
  }

  const result = parseInt(value) / Math.pow(10, tokenDecimals);

  if (result.toString().includes('e')) {
    // formatting to remove scientific notation
    // so instead of 1e-6 we get 0.000001
    return result.toFixed(parseInt(result.toString().split('-')[1]));
  }

  return result;
};

export const formatCurrency = (
  value: string,
  decimals?: number | undefined
) => {
  const tokenDecimals = decimals || 18;
  const max256 = parseInt(
    '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff',
    16
  ).toString(16);

  const max160 = parseInt(
    '0xffffffffffffffffffffffffffffffffffffffff',
    16
  ).toString(16);

  if (
    parseInt(value).toString(16) === max256 ||
    parseInt(value).toString(16) === max160
  ) {
    return 'Infinite';
  }

  const result = parseInt(value) / Math.pow(10, tokenDecimals);

  if (result === 0) {
    return 0;
  }

  if (result < 0.001) {
    return '< 0.001';
  }

  const roundedResult = Math.round(result * 1000) / 1000;

  return roundedResult;
};

export const reduceAddress = (address: string) => {
  if (address.length < 12) {
    return address.charAt(0).toUpperCase() + address.slice(1);
  }
  try {
    if (address !== 'validators') {
      return address.slice(0, 4) + '...' + address.slice(-4);
    }
    return address.charAt(0).toUpperCase() + address.slice(1);
  } catch (error) {
    return '';
  }
};

export const reduceName = (name: string | undefined | null) => {
  if (!name) return undefined;

  if (name.length > 20) {
    return `${name.slice(0, 21)}...`;
  } else return name;
};

export const getBlockExplorerUrl = (input: string, chainId: string): string => {
  return NETWORK_INFO[chainId]
    ? `${NETWORK_INFO[chainId].explorer}address/${input}`
    : '';
};

export const getContractName = (
  address: string,
  network: string
): string | undefined | null => {
  if (!address) return null;
  if (address.substring(0, 2) !== '0x') return address; //If the address was already labeled, return it

  if (!contracts[network]) {
    return null;
  }

  return contracts[network][address.toLowerCase()]
    ? contracts[network][address.toLowerCase()].name
    : null;
};

export const fetchBrowserInformation = async (): Promise<
  string | undefined
> => {
  // currently, Bowser doesn't distinguish between Chrome and Brave.
  // Brave has it's own unqiue detection method.
  // Bowser correctly detects Firefox, Chrome, IE, Opera, and Safari.

  let isBrave = false;
  try {
    isBrave =
      ((navigator as any).brave &&
        (await (navigator as any).brave.isBrave())) ||
      false;
  } catch (error) {
    console.log('brave detection error');
  }
  if (isBrave) {
    return 'Brave';
  }

  return Bowser.parse(window.navigator.userAgent).browser.name;
};

const provider = new ethers.providers.AlchemyProvider(
  'homestead',
  'G-Uc_pdqbIAwHnom2xALqLjaYIJbPDvN'
);

export const resolveENSName = async (
  address: string
): Promise<string | null> => {
  try {
    return address.substring(0, 2) !== '0x' //if we don't find basic address formatting just return null since we know it'll fail
      ? null
      : provider.lookupAddress(address);
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const getAddressLabel = async (
  address: string,
  network: string
): Promise<string | null> => {
  const addressLabel =
    getContractName(address, network) ??
    (network === '0x1' ? resolveENSName(address) : null); //only attempt to resolve ENS on mainnet
  return addressLabel;
};

export const hexToUtf8 = (hexString: string) => {
  try {
    const decoded = Buffer.from(hexString[0].slice(2), 'hex').toString('utf-8');
    return decoded.replace(/\n/g, '\n');
  } catch (e) {
    return hexString;
  }
};

//strip everything before and after the base url
export const getBaseUrl = (sourceUrl: string) => {
  return sourceUrl?.replace(/^https?:\/\/([^/]+).*$/, '$1');
};

export const SplitNetBalanceChanges = (
  netBalanceChanges: any[],
  userAddress: string
) => {
  const incomingTxs: any[] = [];
  const outgoingTxs: any[] = [];
  const validators: any[] = [];

  if (!!netBalanceChanges?.length) {
    netBalanceChanges.forEach((transaction: any) => {
      if (transaction.to === 'validators') {
        validators.push(transaction);
      } else if (
        ['erc20Approve', 'erc721Approve', 'erc1155Approve'].includes(
          transaction.type
        )
      ) {
        outgoingTxs.push({
          ...transaction,
        });
      } else if (
        transaction?.from?.toLowerCase() === userAddress.toLowerCase()
      ) {
        outgoingTxs.push(transaction);
      } else {
        incomingTxs.push(transaction);
      }
    });
  }

  return { incomingTxs, outgoingTxs, validators };
};

export const isFireWallet = (search: string) => {
  const searchParams = new URLSearchParams(search);

  const rpcPayload = searchParams.get('rpcPayload') || '{}';

  const parsedJson: TransactionSimulationRequest = JSON.parse(rpcPayload);

  const isFireWallet = parsedJson.walletProvider === 'Fire Wallet';

  return isFireWallet;
};

export const parseSpenderAddress = (json: any) => {
  try {
    const data = JSON.parse(json);

    if (data && typeof data === 'object') {
      const findSpender = (obj: any): any => {
        if ('spender' in obj) {
          return obj.spender;
        } else if ('offerer' in obj) {
          return obj.offerer; // Seaport
        } else if ('trader' in obj) {
          return obj.trader; // Blur
        }

        for (const key in obj) {
          if (typeof obj[key] === 'object') {
            const result = findSpender(obj[key]);
            if (result) {
              return result;
            }
          }
        }

        return undefined;
      };

      return findSpender(data);
    }
  } catch (error) {
    console.error(error);
  }

  return undefined;
};

export const parseApprovalAddress = (transactionData: string): string | undefined => {
  // method ids for different approve methods
  const methodIds = ['0x095ea7b3', '0xa22cb465'];

  // method id is the first 4 bytes (8 hex characters)
  const methodId = transactionData.slice(0, 10);

  // remove the '0x' prefix and the method id
  const paramsData = transactionData.slice(10);

  // each parameter is 32 bytes (64 hex characters)
  const addressParam = paramsData.slice(0, 64);

  // remove leading zeros from the address and add '0x' prefix
  const address = '0x' + addressParam.replace(/^0+/, '').toLowerCase();

  if (methodIds.includes(methodId)) {
    return address;
  }
};
